// Hoja de cobranza de la ODD (Rodrigo, 2026-10-24): cuando un pedido va con «el cliente cancela la
// factura anterior a camión», el transporte lleva impresa la cartola de lo que el cliente debe, para
// cobrarla al entregar. Hoy se imprimía la cartola a mano; aquí sale sola con los documentos de la
// ODD, una hoja por cliente: lo pendiente (con su vencimiento), el total a cobrar y dónde anotar el
// pago (efectivo, cheque, transferencia) con la firma de quien paga y de quien recibe.
import { useQueries } from '@tanstack/react-query';
import { api } from '../api/cliente';
import { fecha, pesos } from '../comun/formato';
import { EncabezadoImpreso } from '../comun/Imprimir';
import type { DocumentoConSaldo } from '../tesoreria/datos';
import type { Od } from './datos';

const TIPO: Record<number, string> = {
  33: 'Factura',
  34: 'Factura exenta',
  0: 'Vale',
  39: 'Boleta',
  56: 'N. débito',
};

/** Los clientes de la ODD que pagan lo pendiente al recibir (notas activas marcadas). */
export function clientesContraEntrega(od: Od) {
  const vistos = new Map<string, Od['notas'][number]>();
  for (const n of od.notas)
    if (n.estado !== 'RETIRADA' && n.cobroContraEntrega && !vistos.has(n.clienteEntidadId))
      vistos.set(n.clienteEntidadId, n);
  return [...vistos.values()];
}

export function HojaCobranzaImpresa({
  empresaId,
  empresa,
  od,
}: {
  empresaId: string;
  empresa: string;
  od: Od;
}) {
  const clientes = clientesContraEntrega(od);
  const docs = useQueries({
    queries: clientes.map((n) => ({
      queryKey: ['emp', empresaId, 'cxc-documentos', n.clienteEntidadId, 'abiertos'],
      queryFn: () =>
        api.get<DocumentoConSaldo[]>('/tesoreria/cxc/documentos', {
          query: { empresaId, clienteEntidadId: n.clienteEntidadId, abiertos: 'true' },
        }),
    })),
  });
  if (docs.some((d) => d.isPending))
    return <p className="apagado">Preparando la hoja de cobranza…</p>;
  return (
    <>
      {clientes.map((n, i) => {
        const abiertos = (docs[i]?.data ?? []).filter((d) => Number(d.saldo) > 0);
        const total = abiertos.reduce((s, d) => s + Number(d.saldo), 0);
        return (
          <article
            key={n.clienteEntidadId}
            className="impreso hoja-cobranza"
            data-hoja-cobranza={n.clienteEntidadId}
          >
            <EncabezadoImpreso
              empresa={empresa}
              titulo="Hoja de cobranza · cobrar al entregar"
              numero={`ODD N° ${od.numero}`}
              detalle={
                <dl className="impreso-datos">
                  <dt>Cliente</dt>
                  <dd>
                    <b>{n.cliente}</b> {n.clienteRut ?? ''}
                  </dd>
                  <dt>Dirección</dt>
                  <dd>{n.direccionEntrega ?? '—'}</dd>
                  <dt>Pedido</dt>
                  <dd>{n.numeroVisible}</dd>
                  <dt>Fecha despacho</dt>
                  <dd>{fecha(od.fechaNegocio)}</dd>
                </dl>
              }
            />
            <table className="impreso-tabla">
              <thead>
                <tr>
                  <th>Documento</th>
                  <th>N°</th>
                  <th>Emisión</th>
                  <th>Vence</th>
                  <th className="der">Monto</th>
                  <th className="der">Saldo a cobrar</th>
                </tr>
              </thead>
              <tbody>
                {abiertos.map((d) => (
                  <tr key={d.documentoId}>
                    <td>{TIPO[d.tipoDte] ?? `DTE ${d.tipoDte}`}</td>
                    <td className="mono">{d.folio ?? '—'}</td>
                    <td>{fecha(d.fechaEmision)}</td>
                    <td>
                      {fecha(d.fechaVencimiento)}
                      {d.diasVencido > 0 ? ` · ${d.diasVencido} días vencida` : ''}
                    </td>
                    <td className="der mono">{pesos(d.montoTotal)}</td>
                    <td className="der mono">
                      <b>{pesos(d.saldo)}</b>
                    </td>
                  </tr>
                ))}
                {!abiertos.length && (
                  <tr>
                    <td colSpan={6}>Sin documentos pendientes a la fecha de impresión.</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} className="der">
                    <b>TOTAL A COBRAR</b>
                  </td>
                  <td className="der mono">
                    <b>{pesos(String(total))}</b>
                  </td>
                </tr>
              </tfoot>
            </table>
            <table className="impreso-tabla hoja-cobranza-pago">
              <thead>
                <tr>
                  <th>Forma de pago</th>
                  <th>Banco / N° operación</th>
                  <th>N° cheque</th>
                  <th>Fecha cheque</th>
                  <th className="der">Monto</th>
                </tr>
              </thead>
              <tbody>
                {['Efectivo', 'Cheque', 'Cheque', 'Transferencia'].map((m, k) => (
                  <tr key={k}>
                    <td>{m}</td>
                    <td />
                    <td />
                    <td />
                    <td />
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="hoja-cobranza-firmas">
              <span>Paga (nombre y RUT): ______________________________</span>
              <span>Firma: ____________________</span>
              <span>Recibe (transporte): ______________________________</span>
            </div>
          </article>
        );
      })}
    </>
  );
}
